import React from "react";
import dayjs from "dayjs";
import { uniq } from "lodash";
import { LoadingButton } from "@mui/lab";
import { Badge, Drawer, Typography } from "@mui/material";

import { Utils } from "utils";
import { useCollectingData } from "hooks/collecting";
import { useSideBarTags } from "hooks/side-bar-tags";

import { CheckboxList, RenderItemInfo } from "./list";

interface Props {
  selected: string[];
  loading?: boolean;
  disabled?: string[];
  countryCode?: string;
  onSelect: (values: string[]) => void;
  style?: React.CSSProperties;
  buttonDisabled?: boolean;
  selectWithIncluded?: boolean;
}

export const TagsModalSelector = ({
  style,
  loading,
  selected,
  onSelect,
  disabled,
  countryCode,
  buttonDisabled,
  selectWithIncluded = false,
}: Props) => {
  const { data, loading: tagsLoading } = useSideBarTags();
  const { loading: collectiongLoading, data: collectingData } =
    useCollectingData(countryCode);

  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  const isSelectedCategory = React.useCallback(
    (name: string) => {
      const items = data.find((item) => item.name === name)?.items ?? [];
      const values = items.map((item) => item.name);
      return selected.filter((i) => values.includes(i)).length === items.length;
    },
    [selected, data]
  );

  const listData = React.useMemo(() => {
    return data.map((item) => ({
      category: item.name,
      items: item.items.map((i) => i.name),
    }));
  }, [data]);

  const findCategoryItem = React.useCallback(
    (props: RenderItemInfo) => {
      const items = data.find((i) => i.name === props.category)?.items;
      const item = items?.at(props.index);
      return item;
    },
    [data]
  );

  const renderListPrimaryItem = React.useCallback(
    (props: RenderItemInfo) => {
      const item = findCategoryItem(props);

      if (item) {
        const { linkedWith } = item;

        return (
          <React.Fragment>
            <Typography component="span" variant="body2">
              {item.name}
            </Typography>
            {linkedWith.length > 0 && (
              <Typography
                component="span"
                variant="caption"
                color="blueviolet"
              >{` -  (Includes: ${linkedWith.join(", ")})`}</Typography>
            )}
          </React.Fragment>
        );
      }

      return "-/-";
    },
    [findCategoryItem]
  );

  const renderListSecondaryItem = React.useCallback(
    (props: RenderItemInfo) => {
      const item = findCategoryItem(props);

      if (item) {
        const { breaktime } = item;
        const logItem = collectingData.find((i) => i.tag === item?.name);

        const time = Utils.Helpers.getHours(breaktime);
        const breakText = `Break: ${time.h}:${time.m}`;

        const lastUpdated = logItem
          ? `${dayjs.unix(logItem.createdAt.seconds).format("MMM D, YYYY")} (${
              logItem.places
            } items)`
          : "-/-";

        return (
          <React.Fragment>
            <Typography variant="subtitle2" component="span">
              {breakText}
            </Typography>
            {lastUpdated && (
              <Typography variant="subtitle2" component="span">
                {`, ${lastUpdated}`}
              </Typography>
            )}
          </React.Fragment>
        );
      }

      return "-/-";
    },
    [findCategoryItem, collectingData]
  );

  const handleSelectItem = React.useCallback(
    (props: RenderItemInfo) => {
      const item = findCategoryItem(props);

      if (item) {
        let values: string[] = [];
        const { linkedWith, name } = item;
        const isSelected = selected.includes(name);

        if (selectWithIncluded) {
          values = isSelected
            ? selected.filter((value) => {
                if (linkedWith.includes(value)) return false;
                return name !== value;
              })
            : [...selected, name, ...linkedWith];
        }

        if (!selectWithIncluded) {
          values = isSelected
            ? selected.filter((i) => i !== name)
            : [...selected, name];
        }

        onSelect(uniq(values));
      }
    },
    [onSelect, selected, findCategoryItem, selectWithIncluded]
  );

  const handleSelectCategory = React.useCallback(
    (category: string) => {
      const items = data.find((item) => item.name === category)?.items;
      const values = items?.map((item) => item.name) ?? [];

      const isSelected = isSelectedCategory(category);

      let updatedValues: string[] = [];

      if (isSelected) {
        updatedValues = selected.filter((i) => !values.includes(i));
      } else {
        updatedValues = uniq([...selected, ...values]);
      }

      onSelect(updatedValues);
    },
    [data, onSelect, isSelectedCategory, selected]
  );

  return (
    <Badge badgeContent={selected.length} color="primary">
      <LoadingButton
        style={style}
        variant="outlined"
        disabled={buttonDisabled}
        onClick={() => setIsDrawerOpen(true)}
        loading={loading || tagsLoading || collectiongLoading}
      >
        Select Tags
      </LoadingButton>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      >
        <CheckboxList
          data={listData}
          selected={selected}
          disabled={disabled}
          onSelect={handleSelectItem}
          renderPrimary={renderListPrimaryItem}
          onSelectCategory={handleSelectCategory}
          renderSecondary={renderListSecondaryItem}
        />
      </Drawer>
    </Badge>
  );
};
